import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SessionExpiredHandler } from './components/SessionExpiredHandler';
import { AuthProvider } from './contexts/AuthContext';
import { DriveProvider } from './contexts/DriveContext';
import { HouseholdProvider } from './contexts/HouseholdContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Welcome } from './pages/Welcome';
import { PasswordRecovery } from './pages/PasswordRecovery';
import { JoinHousehold } from './pages/JoinHousehold';

const Dashboard = lazy(() =>
  import('./pages/BudgetDashboard').then(m => ({ default: m.Dashboard }))
);
const DataTable = lazy(() =>
  import('./pages/DataTable').then(m => ({ default: m.DataTable }))
);
const ReportPage = lazy(() =>
  import('./pages/ReportPage').then(m => ({ default: m.ReportPage }))
);
const Preferences = lazy(() =>
  import('./pages/Preferences').then(m => ({ default: m.Preferences }))
);
const Categories = lazy(() =>
  import('./pages/Categories').then(m => ({ default: m.Categories }))
);
const Help = lazy(() =>
  import('./pages/Help').then(m => ({ default: m.Help }))
);
const InviteFriends = lazy(() =>
  import('./pages/InviteFriends').then(m => ({ default: m.InviteFriends }))
);
const Onboarding = lazy(() =>
  import('./pages/Onboarding').then(m => ({ default: m.Onboarding }))
);
const Setup = lazy(() =>
  import('./pages/Setup').then(m => ({ default: m.Setup }))
);
const Accounts = lazy(() =>
  import('./pages/Accounts').then(m => ({ default: m.Accounts }))
);
const More = lazy(() =>
  import('./pages/More').then(m => ({ default: m.More }))
);
const Assistant = lazy(() =>
  import('./pages/Assistant').then(m => ({ default: m.Assistant }))
);
const HouseholdDashboard = lazy(() =>
  import('./pages/HouseholdDashboard').then(m => ({
    default: m.HouseholdDashboard,
  }))
);
function App() {
  return (
    <AuthProvider>
      <HouseholdProvider>
        <DriveProvider>
          <PreferencesProvider>
            <Toaster position="top-right" richColors closeButton />
            <BrowserRouter>
              <SessionExpiredHandler />
              <Routes>
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route
                  path="/forgot-password"
                  element={<PasswordRecovery key="request" />}
                />
                <Route
                  path="/reset-password"
                  element={<PasswordRecovery key="confirm" confirm />}
                />
                <Route path="/join-household" element={<JoinHousehold />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route
                    path="assistant"
                    element={
                      <Suspense fallback={null}>
                        <Assistant />
                      </Suspense>
                    }
                  />
                  <Route
                    path="budget"
                    element={
                      <Suspense fallback={null}>
                        <Dashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="transactions"
                    element={
                      <Suspense fallback={null}>
                        <DataTable />
                      </Suspense>
                    }
                  />
                  <Route
                    path="cashflow"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route
                    path="transactions/categories"
                    element={
                      <Suspense fallback={null}>
                        <Categories />
                      </Suspense>
                    }
                  />
                  <Route
                    path="help"
                    element={
                      <Suspense fallback={null}>
                        <Help />
                      </Suspense>
                    }
                  />
                  <Route
                    path="invite"
                    element={
                      <Suspense fallback={null}>
                        <InviteFriends />
                      </Suspense>
                    }
                  />
                  <Route
                    path="dashboard"
                    element={
                      <Suspense fallback={null}>
                        <ReportPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="profile"
                    element={
                      <Navigate to="/preferences?section=account" replace />
                    }
                  />
                  <Route
                    path="preferences"
                    element={
                      <Suspense fallback={null}>
                        <Preferences />
                      </Suspense>
                    }
                  />
                  <Route
                    path="onboarding"
                    element={
                      <Suspense fallback={null}>
                        <Onboarding />
                      </Suspense>
                    }
                  />
                  <Route
                    path="setup"
                    element={
                      <Suspense fallback={null}>
                        <Setup />
                      </Suspense>
                    }
                  />
                  <Route
                    path="accounts"
                    element={
                      <Suspense fallback={null}>
                        <Accounts />
                      </Suspense>
                    }
                  />
                  <Route
                    path="household"
                    element={
                      <Suspense fallback={null}>
                        <HouseholdDashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <Suspense fallback={null}>
                        <Preferences />
                      </Suspense>
                    }
                  />
                  <Route
                    path="more"
                    element={
                      <Suspense fallback={null}>
                        <More />
                      </Suspense>
                    }
                  />
                </Route>
              </Routes>
            </BrowserRouter>
          </PreferencesProvider>
        </DriveProvider>
      </HouseholdProvider>
    </AuthProvider>
  );
}

export default App;
